"use client";

import {
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	frameAt,
	HERO_SCENES,
	type HeroFrame,
	STILL_FRAME,
	sceneDuration,
} from "./timeline";

/** Where the hero is: read every frame by the 3D layer, without re-rendering React. */
export interface HeroClock {
	scene: number;
	t: number;
	playing: boolean;
}

const sameFrame = (a: HeroFrame, b: HeroFrame) =>
	a.scene === b.scene &&
	a.typed === b.typed &&
	a.sent === b.sent &&
	a.step === b.step &&
	a.reply === b.reply &&
	a.cards === b.cards &&
	a.added === b.added &&
	a.speaking === b.speaking &&
	a.elapsed === b.elapsed &&
	a.spoken.length === b.spoken.length &&
	a.spoken.at(-1)?.text === b.spoken.at(-1)?.text;

/**
 * Plays the hero's conversations while `target` is on screen and the tab is visible. The
 * chat re-renders only when what it shows changes (a typed letter, a card); the 3D layer
 * reads `clock` every animation frame. With reduced motion it holds the still frame, and it
 * stops for good once `stopped` (the visitor is talking to the real store).
 */
export function useHeroClock(
	target: RefObject<HTMLElement | null>,
	stopped: boolean,
) {
	const clock = useRef<HeroClock>({ scene: 0, t: 0, playing: false });
	const [frame, setFrame] = useState<HeroFrame>(STILL_FRAME);
	/** Counts scene starts (played or picked), so the tabs' progress bars restart with them. */
	const [run, setRun] = useState(0);
	const [playing, setPlaying] = useState(false);

	/** Picks a scene from its tab: it plays from the start, or shows its end with reduced motion. */
	const jump = useCallback((scene: number) => {
		clock.current.scene = scene;
		clock.current.t = 0;
		setRun((n) => n + 1);
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			setFrame(frameAt(scene, sceneDuration(scene)));
		}
	}, []);

	useEffect(() => {
		if (
			stopped ||
			window.matchMedia("(prefers-reduced-motion: reduce)").matches
		) {
			return;
		}
		let visible = false;
		// Plays only once the page has loaded and gone idle: until then the phone holds its still
		// frame, and the loop's DOM updates stay out of the way of the first paint.
		let settled = false;
		let idle: number | undefined;
		let raf = 0;
		let last = 0;
		let current = STILL_FRAME;
		// Start from the beginning of the first conversation, not its still frame.
		clock.current = { scene: 0, t: 0, playing: false };

		const tick = (now: number) => {
			const c = clock.current;
			c.t += last ? Math.min(now - last, 64) : 0;
			last = now;
			if (c.t > sceneDuration(c.scene)) {
				c.scene = (c.scene + 1) % HERO_SCENES.length;
				c.t = 0;
				setRun((n) => n + 1);
			}
			const next = frameAt(c.scene, c.t);
			if (!sameFrame(next, current)) {
				current = next;
				setFrame(next);
			}
			raf = requestAnimationFrame(tick);
		};
		const play = () => {
			if (!settled || raf || !visible || document.hidden) {
				return;
			}
			clock.current.playing = true;
			setPlaying(true);
			last = 0;
			raf = requestAnimationFrame(tick);
		};
		const pause = () => {
			cancelAnimationFrame(raf);
			raf = 0;
			clock.current.playing = false;
			setPlaying(false);
		};

		const observer = new IntersectionObserver(([entry]) => {
			visible = Boolean(entry?.isIntersecting);
			if (visible) {
				play();
			} else {
				pause();
			}
		});
		if (target.current) {
			observer.observe(target.current);
		}
		const onVisibility = () => (document.hidden ? pause() : play());
		document.addEventListener("visibilitychange", onVisibility);
		const begin = () => {
			settled = true;
			play();
		};
		const settle = () => {
			if ("requestIdleCallback" in window) {
				idle = window.requestIdleCallback(begin, { timeout: 2000 });
			} else {
				begin();
			}
		};
		if (document.readyState === "complete") {
			settle();
		} else {
			window.addEventListener("load", settle, { once: true });
		}
		return () => {
			// The frame `tick` scheduled last: no tick runs once the effect is gone.
			cancelAnimationFrame(raf);
			window.removeEventListener("load", settle);
			if (idle !== undefined) {
				window.cancelIdleCallback(idle);
			}
			pause();
			observer.disconnect();
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [target, stopped]);

	return { frame, clock, run, playing, jump };
}
